import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningIcon from "@mui/icons-material/Warning";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useNavigate } from "react-router-dom";

import StandartButton from "../../components/atoms/StandartButton";
import { ButtonVariant } from "../../Constant/button";
import {
    AddCard,
    Badge,
    BasePath,
    CardList,
    ChevronButton,
    Column,
    ColumnTitle,
    CompactBadgeGroup,
    CompactCardList,
    CompactDangerBadge,
    CompactDetailPopover,
    CompactDetailRow,
    CompactItemCard,
    CompactSuccessBadge,
    CompactWarningBadge,
    DetailRow,
    DetailRowText,
    FlowColumns,
    FlowPanel,
    FlowPath,
    FlowSvg,
    HoverFlowSvg,
    IconChip,
    ItemCard,
    ItemDetail,
    ItemDetailInner,
    ItemHeader,
    ItemLabel,
    LogoBadge,
    MobileActionButton,
    MobileActionsRow,
    MobileEmptyHint,
    MobileIconButton,
    MobileIconRow,
    MobileSegmentedControl,
    MobileSegmentTab,
    MobileTrackerCard,
    MobileTrackerColumn,
    MobileTrackerList,
    MobileTrackerName,
    MobileTrackerRow,
    StatusDot,
} from "./FlowDiagram.styled";

const ACTIVE_COLOR = "#45c300";
const DANGER_ICON_COLOR = "#d32f2f";
const EMPTY_COLOR = "#c7c7cc";
// Must match LogoBadge's 72px diameter (see FlowDiagram.styled.js) so
// compact-mode connector lines converge exactly on the hub circle's edge.
const HUB_RADIUS = 36;
const HUB_TOOLTIP_TEXT =
    "Signalsight aggregates events from every connected source and forwards them to every connected destination.";

const flattenTrackers = (trackers) => {
    const sources = [];
    const destinations = [];
    (trackers || []).forEach((tracker) => {
        if (!tracker) return;
        tracker.sources?.forEach((source) => {
            sources.push({
                ...source,
                tracker_name: tracker.tracker_name,
                tracker_id: tracker.id,
            });
        });
        tracker.destinations?.forEach((destination) => {
            destinations.push({
                ...destination,
                tracker_name: tracker.tracker_name,
                tracker_id: tracker.id,
            });
        });
    });
    return { sources, destinations };
};

const buildGroups = (items, typeKey, typeNameOf, typeIdOf, iconResolver) => {
    const groups = new Map();
    items.forEach((item) => {
        const key = String(item[typeKey]);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
    });
    return Array.from(groups.entries()).map(([key, groupItems]) => {
        const first = groupItems[0];
        const hasFailure = groupItems.some((item) => item?.status === 0);
        const hasActive = groupItems.some((item) => item?.status !== 0);
        const activeCount = groupItems.filter(
            (item) => item?.status !== 0,
        ).length;
        const inactiveCount = groupItems.filter(
            (item) => item?.status === 0,
        ).length;
        return {
            // Prefixed with typeKey (distinct per side) so a source group and
            // a destination group that happen to share a numeric type id
            // don't collide in the shared `expanded`/ref-map state below.
            key: `${typeKey}-${key}`,
            label: typeNameOf(first),
            icon: iconResolver(typeIdOf(first)),
            items: groupItems,
            single: groupItems.length === 1,
            badge: groupItems.length > 1 ? groupItems.length : null,
            status: hasFailure ? "attention" : "healthy",
            hasActive,
            activeCount,
            inactiveCount,
            trackerId: first.tracker_id,
        };
    });
};

// Every item gets its own card instead of being grouped by type — used by
// pages (e.g. SingleTracker) that need one card per source/destination with
// fully custom, per-item interactive content (modals, switches, etc).
const buildUngroupedGroups = (items, side) =>
    items.map((item, index) => ({
        key: `${side}-${item.id ?? `${item.tracker_id}-${index}`}`,
        items: [item],
        single: true,
        badge: null,
        status: item?.status === 0 ? "attention" : "healthy",
        hasActive:
            item?.status !== 0 &&
            (side !== "destination" || item?.tracker_status === 1),
        trackerId: item.tracker_id,
    }));

const FlowDiagram = ({
    trackers,
    getSourceIcon,
    getDestinationIcon,
    onNavigateTracker,
    onHubClick,
    onAddSource,
    onAddDestination,
    sourcesTitle,
    destinationsTitle,
    addSourceLabel,
    addDestinationLabel,
    header,
    ungrouped = false,
    renderSourceCard,
    renderDestinationCard,
    sourceTrailingContent,
    destinationTrailingContent,
    emptySourceContent,
    emptyDestinationContent,
    columnWidth,
    bare,
    compact = false,
    sourceWarningTooltip = "Since the Access Token is expired, you must reconnect the source.",
    destinationWarningTooltip = "Since the Access Token is expired, you must reconnect the destination.",
    hideMobileSegmentedControl = false,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

    const showMobileTrackerCards = isMobileOrTablet && !ungrouped;

    const { sources, destinations } = useMemo(
        () => flattenTrackers(trackers),
        [trackers],
    );

    const sourceGroups = useMemo(
        () =>
            ungrouped
                ? buildUngroupedGroups(sources, "source")
                : buildGroups(
                      sources,
                      "source_type_id",
                      (item) => item?.source_type?.name,
                      (item) => item?.source_type?.id,
                      getSourceIcon,
                  ),
        [sources, getSourceIcon, ungrouped],
    );

    const destinationGroups = useMemo(
        () =>
            ungrouped
                ? buildUngroupedGroups(destinations, "destination")
                : buildGroups(
                      destinations,
                      "destination_type_id",
                      (item) => item?.destination_type?.name,
                      (item) => item?.destination_type?.id,
                      getDestinationIcon,
                  ),
        [destinations, getDestinationIcon, ungrouped],
    );

    const anySourceActive = useMemo(
        () => sourceGroups.some((group) => group.hasActive),
        [sourceGroups],
    );

    const [expanded, setExpanded] = useState({});

    const [hoverTarget, setHoverTarget] = useState(null);
    const [lockedTarget, setLockedTarget] = useState(null);
    const isTouchDevice =
        typeof window !== "undefined" &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    const activeTarget = lockedTarget || hoverTarget;

    const activeHoveredGroup =
        activeTarget?.type === "source"
            ? sourceGroups.find((group) => group.key === activeTarget.key)
            : activeTarget?.type === "destination"
              ? destinationGroups.find(
                    (group) => group.key === activeTarget.key,
                )
              : null;

    const getLineOpacity = (line, baseOpacity, dimmedOpacity = 0.2) => {
        if (!activeTarget) return baseOpacity;
        if (activeTarget.type === "hub") return baseOpacity;
        if (line.side === activeTarget.type) {
            return line.groupKeys?.includes(activeTarget.key)
                ? baseOpacity
                : dimmedOpacity;
        }
        return baseOpacity;
    };

    // A hovered/locked source (or destination) that isn't itself active
    // sends nothing through the hub, so the opposite side shouldn't read as
    // "healthy" either — shared by line coloring and the status dots below.
    const isOppositeSideForcedInactive = (side) => {
        if (!activeTarget || activeTarget.type === "hub") return false;
        if (side === activeTarget.type) return false;
        return !!activeHoveredGroup && !activeHoveredGroup.hasActive;
    };

    const isLineForcedInactive = (line) =>
        isOppositeSideForcedInactive(line.side);

    const isMatchedButInactive = (line) => {
        if (!activeTarget || activeTarget.type === "hub") return false;
        if (line.side !== activeTarget.type) return false;
        if (!line.groupKeys?.includes(activeTarget.key)) return false;
        return !!activeHoveredGroup && !activeHoveredGroup.hasActive;
    };

    const isBackboneSuppressed = (line) =>
        !!line.isBackbone && !!activeTarget && activeTarget.type !== "hub";

    const getLineColor = (line) =>
        isBackboneSuppressed(line) ||
        isLineForcedInactive(line) ||
        isMatchedButInactive(line)
            ? EMPTY_COLOR
            : line.color;
    const isLineAnimated = (line) => {
        if (isBackboneSuppressed(line)) return false;
        if (!activeTarget || activeTarget.type === "hub") return true;
        if (line.side !== activeTarget.type) return true;
        return !!line.groupKeys?.includes(activeTarget.key);
    };

    const getGroupColor = (group, side) => {
        if (isOppositeSideForcedInactive(side)) return EMPTY_COLOR;
        const shouldAnimate =
            group.hasActive && (side === "source" || anySourceActive);
        return shouldAnimate ? ACTIVE_COLOR : EMPTY_COLOR;
    };

    const getHoverOnlyLineOpacity = (line, baseOpacity) => {
        if (!activeTarget || activeTarget.type === "hub") return 0;
        if (activeTarget.type !== line.side) return 0;
        return line.groupKeys.includes(activeTarget.key) ? baseOpacity : 0;
    };

    const getCardOpacity = (group, side) => {
        if (!activeTarget) return 1;
        if (activeTarget.type === "hub") return 1;
        if (activeTarget.type === side) {
            return activeTarget.key === group.key ? 1 : 0.2;
        }
        return 1;
    };

    const [animateLines, setAnimateLines] = useState(false);

    const suppressResizeMeasureRef = useRef(false);
    const toggleSettleTimeout = useRef(null);
    const toggle = (key) => {
        setAnimateLines(true);
        suppressResizeMeasureRef.current = true;
        if (toggleSettleTimeout.current)
            clearTimeout(toggleSettleTimeout.current);
        toggleSettleTimeout.current = setTimeout(() => {
            suppressResizeMeasureRef.current = false;
        }, 280);
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const lineTransitionStyle = useMemo(() => {
        const parts = ["opacity 180ms ease"];
        if (animateLines) parts.push("d 260ms ease");
        return { transition: parts.join(", ") };
    }, [animateLines]);
    const badgeTransitionStyle = useMemo(() => {
        const parts = [];
        if (animateLines) {
            parts.push("left 260ms ease", "top 260ms ease");
        }
        if (onHubClick) {
            parts.push(
                "transform 160ms ease",
                "border-color 160ms ease",
                "box-shadow 160ms ease",
            );
        }
        return { transition: parts.length > 0 ? parts.join(", ") : "none" };
    }, [animateLines, onHubClick]);

    const containerRef = useRef(null);
    const sourceRefs = useRef({});
    const destinationRefs = useRef({});
    const addSourceRef = useRef(null);
    const addDestinationRef = useRef(null);
    const [lines, setLines] = useState([]);

    const [hoverOnlyLines, setHoverOnlyLines] = useState([]);
    const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
    const [trunkMidPoint, setTrunkMidPoint] = useState(null);
    const pendingFrame = useRef(null);

    const measure = useCallback(() => {
        const containerEl = containerRef.current;
        if (!containerEl) return;
        const cRect = containerEl.getBoundingClientRect();

        const edgePoint = (el, side) => {
            if (!el) return null;
            const r = el.getBoundingClientRect();
            return {
                x:
                    side === "right"
                        ? r.right - cRect.left
                        : r.left - cRect.left,
                y: r.top + r.height / 2 - cRect.top,
            };
        };

        const srcPts =
            sourceGroups.length > 0
                ? sourceGroups
                      .map((group) => ({
                          ...group,
                          pt: edgePoint(sourceRefs.current[group.key], "right"),
                      }))
                      .filter((p) => p.pt)
                : [
                      {
                          key: "__add-source",
                          status: "empty",
                          pt: edgePoint(addSourceRef.current, "right"),
                      },
                  ].filter((p) => p.pt);
        const dstPts =
            destinationGroups.length > 0
                ? destinationGroups
                      .map((group) => ({
                          ...group,
                          pt: edgePoint(
                              destinationRefs.current[group.key],
                              "left",
                          ),
                      }))
                      .filter((p) => p.pt)
                : [
                      {
                          key: "__add-destination",
                          status: "empty",
                          pt: edgePoint(addDestinationRef.current, "left"),
                      },
                  ].filter((p) => p.pt);

        if (!srcPts.length && !dstPts.length) {
            setLines([]);
            setTrunkMidPoint(null);
            return;
        }

        const allY = [...srcPts, ...dstPts].map((p) => p.pt.y);
        const trunkX = cRect.width / 2;
        const topY = Math.min(...allY);
        const bottomY = Math.max(...allY);
        const midY = (topY + bottomY) / 2;

        const nextLines = [];
        const nextHoverLines = [];
        let flowIndex = 0;

        const anySrcPtActive = srcPts.some(
            (p) => p.status !== "empty" && p.hasActive !== false,
        );

        if (compact) {
            const hubLeftX = trunkX - HUB_RADIUS;
            const hubRightX = trunkX + HUB_RADIUS;

            const groupIntoRows = (points) => {
                const rows = [];
                points.forEach((p) => {
                    const row = rows.find((r) => Math.abs(r.y - p.pt.y) < 1);
                    if (row) row.points.push(p);
                    else rows.push({ y: p.pt.y, points: [p] });
                });
                return rows;
            };

            const directLine = (side, p) => {
                const { x, y } = p.pt;
                const isEmpty = p.status === "empty";
                if (side === "source") {
                    const c1x = x + (hubLeftX - x) * 0.5;
                    const c2x = hubLeftX - (hubLeftX - x) * 0.5;
                    return {
                        d: `M ${x} ${y} C ${c1x} ${y}, ${c2x} ${midY}, ${hubLeftX} ${midY}`,
                        shouldAnimate: !isEmpty && p.hasActive !== false,
                    };
                }
                const c1x = hubRightX + (x - hubRightX) * 0.5;
                const c2x = x - (x - hubRightX) * 0.5;
                return {
                    d: `M ${hubRightX} ${midY} C ${c1x} ${midY}, ${c2x} ${y}, ${x} ${y}`,
                    shouldAnimate:
                        !isEmpty && p.hasActive !== false && anySrcPtActive,
                };
            };

            const buildSide = (points, side) => {
                groupIntoRows(points).forEach((row) => {
                    const isCenterRow =
                        row.points.length > 1 && Math.abs(row.y - midY) < 1;

                    if (!isCenterRow) {
                        row.points.forEach((p) => {
                            const { d, shouldAnimate } = directLine(side, p);
                            nextLines.push({
                                key: `${side[0]}-${p.key}`,
                                side,
                                groupKeys: [p.key],
                                d,
                                color: shouldAnimate
                                    ? ACTIVE_COLOR
                                    : EMPTY_COLOR,
                                dur: shouldAnimate
                                    ? (1.2 + (flowIndex % 3) * 0.3).toFixed(1) +
                                      "s"
                                    : null,
                                delay: "-" + (flowIndex * 0.3).toFixed(2) + "s",
                            });
                            flowIndex++;
                        });
                        return;
                    }

                    const { y } = row;
                    const rowXs = row.points.map((p) => p.pt.x);
                    const minX = Math.min(...rowXs);
                    const maxX = Math.max(...rowXs);

                    const hubEdgeX = side === "source" ? maxX : minX;
                    const rowKeys = row.points.map((p) => p.key);
                    const rowHasActive = row.points.some(
                        (p) => p.status !== "empty" && p.hasActive !== false,
                    );
                    const rowShouldAnimate =
                        side === "source"
                            ? rowHasActive
                            : rowHasActive && anySrcPtActive;
                    const rowColor = rowShouldAnimate
                        ? ACTIVE_COLOR
                        : EMPTY_COLOR;

                    nextLines.push({
                        key: `${side[0]}-row-${rowKeys.join("_")}`,
                        side,
                        groupKeys: rowKeys,
                        isBackbone: true,
                        d: `M ${minX} ${y} L ${maxX} ${y}`,
                        color: rowColor,
                        dur: rowShouldAnimate
                            ? (1.2 + (flowIndex % 3) * 0.3).toFixed(1) + "s"
                            : null,
                        delay: "-" + (flowIndex * 0.3).toFixed(2) + "s",
                    });
                    flowIndex++;

                    const trunkD =
                        side === "source"
                            ? `M ${hubEdgeX} ${y} C ${hubEdgeX + (hubLeftX - hubEdgeX) * 0.5} ${y}, ${hubLeftX - (hubLeftX - hubEdgeX) * 0.5} ${midY}, ${hubLeftX} ${midY}`
                            : `M ${hubRightX} ${midY} C ${hubRightX + (hubEdgeX - hubRightX) * 0.5} ${midY}, ${hubEdgeX - (hubEdgeX - hubRightX) * 0.5} ${y}, ${hubEdgeX} ${y}`;
                    nextLines.push({
                        key: `${side[0]}-trunk-${rowKeys.join("_")}`,
                        side,
                        groupKeys: rowKeys,
                        d: trunkD,
                        color: rowColor,
                        dur: rowShouldAnimate
                            ? (1.2 + (flowIndex % 3) * 0.3).toFixed(1) + "s"
                            : null,
                        delay: "-" + (flowIndex * 0.3).toFixed(2) + "s",
                    });
                    flowIndex++;

                    row.points
                        .filter((p) => p.pt.x !== hubEdgeX)
                        .forEach((p) => {
                            const { d, shouldAnimate } = directLine(side, p);
                            nextHoverLines.push({
                                key: `${side[0]}-hover-${p.key}`,
                                side,
                                groupKeys: [p.key],
                                d,
                                color: shouldAnimate
                                    ? ACTIVE_COLOR
                                    : EMPTY_COLOR,
                                dur: shouldAnimate
                                    ? (1.2 + (flowIndex % 3) * 0.3).toFixed(1) +
                                      "s"
                                    : null,
                                delay: "-" + (flowIndex * 0.3).toFixed(2) + "s",
                            });
                            flowIndex++;
                        });
                });
            };

            buildSide(srcPts, "source");
            buildSide(dstPts, "destination");
        } else {
            srcPts.forEach((p) => {
                const { x, y } = p.pt;
                const c1x = x + (trunkX - x) * 0.5;
                const c2x = trunkX - (trunkX - x) * 0.5;
                const isEmpty = p.status === "empty";
                // Only animate the flow when at least one item behind this
                // line is actually active — a lone failed source shouldn't
                // pulse.
                const shouldAnimate = !isEmpty && p.hasActive !== false;
                nextLines.push({
                    key: `s-${p.key}`,
                    side: "source",
                    groupKeys: [p.key],
                    d: `M ${x} ${y} C ${c1x} ${y}, ${c2x} ${midY}, ${trunkX} ${midY}`,
                    color: shouldAnimate ? ACTIVE_COLOR : EMPTY_COLOR,
                    dur: shouldAnimate
                        ? (1.2 + (flowIndex % 3) * 0.3).toFixed(1) + "s"
                        : null,
                    delay: "-" + (flowIndex * 0.3).toFixed(2) + "s",
                });
                flowIndex++;
            });

            dstPts.forEach((p) => {
                const { x, y } = p.pt;
                const c1x = trunkX + (x - trunkX) * 0.5;
                const c2x = x - (x - trunkX) * 0.5;
                const isEmpty = p.status === "empty";
                const shouldAnimate =
                    !isEmpty && p.hasActive !== false && anySrcPtActive;
                nextLines.push({
                    key: `d-${p.key}`,
                    side: "destination",
                    groupKeys: [p.key],
                    d: `M ${trunkX} ${midY} C ${c1x} ${midY}, ${c2x} ${y}, ${x} ${y}`,
                    color: shouldAnimate ? ACTIVE_COLOR : EMPTY_COLOR,
                    dur: shouldAnimate
                        ? (1.2 + (flowIndex % 3) * 0.3).toFixed(1) + "s"
                        : null,
                    delay: "-" + (flowIndex * 0.3).toFixed(2) + "s",
                });
                flowIndex++;
            });
        }

        setLines(nextLines);
        setHoverOnlyLines(nextHoverLines);
        setSvgSize({ width: cRect.width, height: cRect.height });
        setTrunkMidPoint({ x: trunkX, y: midY });
    }, [sourceGroups, destinationGroups, compact]);

    const scheduleMeasure = useCallback(() => {
        if (pendingFrame.current || suppressResizeMeasureRef.current) return;
        pendingFrame.current = requestAnimationFrame(() => {
            pendingFrame.current = null;
            setAnimateLines(false);
            measure();
        });
    }, [measure]);

    useLayoutEffect(() => {
        measure();
        const containerEl = containerRef.current;
        const resizeObserver = new ResizeObserver(scheduleMeasure);
        if (containerEl) resizeObserver.observe(containerEl);
        window.addEventListener("resize", scheduleMeasure);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", scheduleMeasure);
            if (pendingFrame.current) {
                cancelAnimationFrame(pendingFrame.current);
                pendingFrame.current = null;
            }
        };
    }, [measure, scheduleMeasure]);

    useEffect(() => {
        const t = setTimeout(measure, 280);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    useEffect(
        () => () => {
            if (toggleSettleTimeout.current)
                clearTimeout(toggleSettleTimeout.current);
        },
        [],
    );

    useEffect(() => {
        const hasExpanded = Object.values(expanded).some(Boolean);
        if (!hasExpanded) return undefined;

        const handleOutsideClick = (e) => {
            const cardRefs = {
                ...sourceRefs.current,
                ...destinationRefs.current,
            };
            const clickedInsideOpenCard = Object.entries(expanded).some(
                ([key, isOpen]) => isOpen && cardRefs[key]?.contains(e.target),
            );
            if (!clickedInsideOpenCard) setExpanded({});
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () =>
            document.removeEventListener("mousedown", handleOutsideClick);
    }, [expanded]);

    useEffect(() => {
        if (!lockedTarget) return undefined;

        const clearIfOutside = (e) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target)
            ) {
                setLockedTarget(null);
            }
        };

        document.addEventListener("mousedown", clearIfOutside);
        document.addEventListener("touchstart", clearIfOutside);
        return () => {
            document.removeEventListener("mousedown", clearIfOutside);
            document.removeEventListener("touchstart", clearIfOutside);
        };
    }, [lockedTarget]);

    const handleHubMouseEnter = () => {
        if (isTouchDevice) return;
        setHoverTarget({ type: "hub", key: "hub" });
    };
    const handleHubMouseLeave = () => {
        if (isTouchDevice) return;
        setHoverTarget((prev) => (prev?.type === "hub" ? null : prev));
    };
    const handleHubClick = () => {
        if (onHubClick) {
            onHubClick();
            return;
        }
        setLockedTarget((prev) =>
            prev?.type === "hub" ? null : { type: "hub", key: "hub" },
        );
    };

    const renderCard = (group, side) => {
        const isOpen = !!expanded[group.key];
        const setRef = (el) => {
            if (side === "source") sourceRefs.current[group.key] = el;
            else destinationRefs.current[group.key] = el;
        };
        const dotColor = getGroupColor(group, side);

        const isLockedHere =
            lockedTarget?.type === side && lockedTarget?.key === group.key;
        const handleMouseEnter = () => {
            if (isTouchDevice) return;
            setHoverTarget({ type: side, key: group.key });
        };
        const handleMouseLeave = () => {
            if (isTouchDevice) return;
            setHoverTarget((prev) =>
                prev?.type === side && prev?.key === group.key ? null : prev,
            );
        };
        // Unlike the compact Trackers-list cards, Home/SingleTracker cards
        // only drive the line highlighting on hover — the cards themselves
        // stay at full opacity regardless of what's hovered.

        const renderCustomContent =
            side === "source" ? renderSourceCard : renderDestinationCard;
        if (renderCustomContent) {
            return (
                <ItemCard
                    key={group.key}
                    ref={setRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {renderCustomContent(group.items[0])}
                    <StatusDot
                        $side={side === "source" ? "right" : "left"}
                        $color={dotColor}
                    />
                </ItemCard>
            );
        }

        // On touch there's no hover, so the first tap previews the highlight
        // instead of firing navigation; tapping the same (already
        // highlighted) card again proceeds as a normal click.
        const handleHeaderClick = () => {
            if (isTouchDevice && !isLockedHere) {
                setLockedTarget({ type: side, key: group.key });
                return;
            }
            setLockedTarget((prev) =>
                prev?.type === side && prev?.key === group.key ? null : prev,
            );
            if (group.single) {
                onNavigateTracker(group.trackerId);
            } else {
                toggle(group.key);
            }
        };

        return (
            <ItemCard
                key={group.key}
                ref={setRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <ItemHeader $clickable onClick={handleHeaderClick}>
                    <IconChip>
                        {group.icon && <img src={group.icon} alt="" />}
                    </IconChip>
                    <ItemLabel>{group.label}</ItemLabel>
                    {group.badge && <Badge>{group.badge}</Badge>}
                    {group.status === "attention" && (
                        <Tooltip
                            placement="top"
                            title={
                                side === "source"
                                    ? sourceWarningTooltip
                                    : destinationWarningTooltip
                            }
                        >
                            <WarningIcon
                                fontSize="small"
                                style={{
                                    color: DANGER_ICON_COLOR,
                                    flexShrink: 0,
                                }}
                            />
                        </Tooltip>
                    )}
                    {!group.single && (
                        <ChevronButton
                            $open={isOpen}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggle(group.key);
                            }}
                        >
                            <KeyboardArrowDownIcon fontSize="small" />
                        </ChevronButton>
                    )}
                </ItemHeader>
                {!group.single && (
                    <ItemDetail $open={isOpen}>
                        <ItemDetailInner>
                            {group.items.map((item) => (
                                <DetailRow
                                    key={`${item.tracker_id}-${item.id ?? item.name}`}
                                    onClick={() =>
                                        onNavigateTracker(item.tracker_id)
                                    }
                                >
                                    <DetailRowText>
                                        <div className="tracker-name">
                                            {item.tracker_name}
                                        </div>
                                        <div className="item-name">
                                            {item.name}
                                        </div>
                                    </DetailRowText>
                                    {item.status === 0 && (
                                        <Tooltip
                                            placement="top"
                                            title={
                                                side === "source"
                                                    ? sourceWarningTooltip
                                                    : destinationWarningTooltip
                                            }
                                        >
                                            <WarningIcon
                                                fontSize="small"
                                                style={{
                                                    color: DANGER_ICON_COLOR,
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </DetailRow>
                            ))}
                        </ItemDetailInner>
                    </ItemDetail>
                )}
                <StatusDot
                    $side={side === "source" ? "right" : "left"}
                    $color={dotColor}
                />
            </ItemCard>
        );
    };

    const renderCompactCard = (group, side) => {
        const isOpen = !!expanded[group.key];
        const setRef = (el) => {
            if (side === "source") sourceRefs.current[group.key] = el;
            else destinationRefs.current[group.key] = el;
        };
        const dotColor = getGroupColor(group, side);

        const isLockedHere =
            lockedTarget?.type === side && lockedTarget?.key === group.key;

        // On touch there's no hover, so the first tap previews the
        // highlight instead of firing navigation; tapping the same
        // (already-highlighted) icon again proceeds as a normal click.
        const handleClick = (e) => {
            if (isTouchDevice && !isLockedHere) {
                e.preventDefault();
                e.stopPropagation();
                setLockedTarget({ type: side, key: group.key });
                return;
            }
            setLockedTarget((prev) =>
                prev?.type === side && prev?.key === group.key ? null : prev,
            );
            if (group.single) {
                onNavigateTracker(group.trackerId);
            } else {
                toggle(group.key);
            }
        };

        const handleMouseEnter = () => {
            if (isTouchDevice) return;
            setHoverTarget({ type: side, key: group.key });
        };
        const handleMouseLeave = () => {
            if (isTouchDevice) return;
            setHoverTarget((prev) =>
                prev?.type === side && prev?.key === group.key ? null : prev,
            );
        };

        return (
            <CompactItemCard
                key={group.key}
                ref={setRef}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    opacity: getCardOpacity(group, side),
                    transition:
                        "opacity 180ms ease, background 150ms ease, border-color 150ms ease",
                }}
            >
                <Tooltip placement="top" title={group.label || ""}>
                    <span
                        style={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {group.icon && <img src={group.icon} alt="" />}
                    </span>
                </Tooltip>
                {group.single ? (
                    group.status === "attention" && (
                        <Tooltip
                            placement="top"
                            title={
                                side === "source"
                                    ? sourceWarningTooltip
                                    : destinationWarningTooltip
                            }
                        >
                            <CompactWarningBadge>
                                <WarningIcon
                                    style={{ fontSize: 12, color: "#ffffff" }}
                                />
                            </CompactWarningBadge>
                        </Tooltip>
                    )
                ) : (
                    <CompactBadgeGroup>
                        {group.inactiveCount > 0 && (
                            <Tooltip
                                placement="top"
                                title={
                                    side === "source"
                                        ? sourceWarningTooltip
                                        : destinationWarningTooltip
                                }
                            >
                                <CompactDangerBadge>
                                    {group.inactiveCount}
                                </CompactDangerBadge>
                            </Tooltip>
                        )}
                        {group.activeCount > 0 && (
                            <CompactSuccessBadge>
                                {group.activeCount}
                            </CompactSuccessBadge>
                        )}
                    </CompactBadgeGroup>
                )}
                {!group.single && isOpen && (
                    <CompactDetailPopover onClick={(e) => e.stopPropagation()}>
                        {group.items.map((item) => (
                            <CompactDetailRow
                                key={`${item.tracker_id}-${item.id ?? item.name}`}
                                onClick={() =>
                                    onNavigateTracker(item.tracker_id)
                                }
                            >
                                <DetailRowText>
                                    <div className="tracker-name">
                                        {item.tracker_name}
                                    </div>
                                    <div className="item-name">{item.name}</div>
                                </DetailRowText>
                                {item.status === 0 && (
                                    <Tooltip
                                        placement="top"
                                        title={
                                            side === "source"
                                                ? sourceWarningTooltip
                                                : destinationWarningTooltip
                                        }
                                    >
                                        <WarningIcon
                                            fontSize="small"
                                            style={{ color: DANGER_ICON_COLOR }}
                                        />
                                    </Tooltip>
                                )}
                            </CompactDetailRow>
                        ))}
                    </CompactDetailPopover>
                )}
                <StatusDot
                    $side={side === "source" ? "right" : "left"}
                    $color={dotColor}
                />
            </CompactItemCard>
        );
    };

    const renderMobileIconGroup = (group, side) => {
        const warningTooltip =
            side === "source"
                ? sourceWarningTooltip
                : destinationWarningTooltip;
        return (
            <Tooltip key={group.key} placement="top" title={group.label || ""}>
                <MobileIconButton
                    onClick={() => onNavigateTracker(group.trackerId)}
                >
                    {group.icon && <img src={group.icon} alt="" />}
                    {group.single ? (
                        group.status === "attention" && (
                            <Tooltip placement="top" title={warningTooltip}>
                                <CompactWarningBadge>
                                    <WarningIcon
                                        style={{
                                            fontSize: 12,
                                            color: "#ffffff",
                                        }}
                                    />
                                </CompactWarningBadge>
                            </Tooltip>
                        )
                    ) : (
                        <CompactBadgeGroup>
                            {group.inactiveCount > 0 && (
                                <Tooltip placement="top" title={warningTooltip}>
                                    <CompactDangerBadge>
                                        {group.inactiveCount}
                                    </CompactDangerBadge>
                                </Tooltip>
                            )}
                            {group.activeCount > 0 && (
                                <CompactSuccessBadge>
                                    {group.activeCount}
                                </CompactSuccessBadge>
                            )}
                        </CompactBadgeGroup>
                    )}
                </MobileIconButton>
            </Tooltip>
        );
    };

    const renderMobileTrackerCard = (tracker) => {
        const withTrackerMeta = (items) =>
            (items || []).map((item) => ({
                ...item,
                tracker_id: tracker.id,
                tracker_name: tracker.tracker_name,
            }));

        const mobileSourceGroups = buildGroups(
            withTrackerMeta(tracker.sources),
            "source_type_id",
            (item) => item?.source_type?.name,
            (item) => item?.source_type?.id,
            getSourceIcon,
        );
        const mobileDestinationGroups = buildGroups(
            withTrackerMeta(tracker.destinations),
            "destination_type_id",
            (item) => item?.destination_type?.name,
            (item) => item?.destination_type?.id,
            getDestinationIcon,
        );

        return (
            <MobileTrackerCard key={tracker.id}>
                <MobileTrackerRow>
                    <MobileTrackerColumn $align="left">
                        <MobileIconRow $justify="flex-start">
                            {mobileSourceGroups.length > 0 ? (
                                mobileSourceGroups.map((group) =>
                                    renderMobileIconGroup(group, "source"),
                                )
                            ) : (
                                <MobileEmptyHint>—</MobileEmptyHint>
                            )}
                        </MobileIconRow>
                    </MobileTrackerColumn>

                    <MobileTrackerColumn $align="center">
                        <MobileTrackerName
                            onClick={() => onNavigateTracker(tracker.id)}
                        >
                            {tracker.tracker_name}
                        </MobileTrackerName>
                    </MobileTrackerColumn>

                    <MobileTrackerColumn $align="right">
                        <MobileIconRow $justify="flex-end">
                            {mobileDestinationGroups.length > 0 ? (
                                mobileDestinationGroups.map((group) =>
                                    renderMobileIconGroup(group, "destination"),
                                )
                            ) : (
                                <MobileEmptyHint>—</MobileEmptyHint>
                            )}
                        </MobileIconRow>
                    </MobileTrackerColumn>
                </MobileTrackerRow>

                <MobileActionsRow>
                    <Tooltip placement="top" title="View">
                        <MobileActionButton
                            onClick={() => onNavigateTracker(tracker.id)}
                        >
                            <VisibilityIcon fontSize="small" />
                        </MobileActionButton>
                    </Tooltip>
                    <Tooltip placement="top" title="Settings">
                        <MobileActionButton
                            onClick={() =>
                                navigate(`/tracker-setting/${tracker.id}`)
                            }
                        >
                            <SettingsOutlinedIcon fontSize="small" />
                        </MobileActionButton>
                    </Tooltip>
                    <Tooltip placement="top" title="Reports">
                        <MobileActionButton
                            onClick={() =>
                                navigate(`/tracker-reports/${tracker.id}`)
                            }
                        >
                            <AnalyticsOutlinedIcon fontSize="small" />
                        </MobileActionButton>
                    </Tooltip>
                </MobileActionsRow>
            </MobileTrackerCard>
        );
    };

    if (showMobileTrackerCards) {
        return (
            <FlowPanel $bare={bare} $mobileFlush>
                {!hideMobileSegmentedControl && (
                    <MobileSegmentedControl>
                        {[sourcesTitle, "Name", destinationsTitle].map(
                            (label) => (
                                <MobileSegmentTab key={label}>
                                    {label}
                                </MobileSegmentTab>
                            ),
                        )}
                    </MobileSegmentedControl>
                )}
                <MobileTrackerList>
                    {(trackers || []).map((tracker) =>
                        renderMobileTrackerCard(tracker),
                    )}
                </MobileTrackerList>
            </FlowPanel>
        );
    }

    return (
        <FlowPanel $bare={bare}>
            {header}
            <FlowColumns ref={containerRef}>
                <FlowSvg width={svgSize.width} height={svgSize.height}>
                    {lines.map((line) => (
                        <BasePath
                            key={line.key}
                            d={line.d}
                            stroke={getLineColor(line)}
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            opacity={getLineOpacity(
                                line,
                                line.dur ? 0.25 : 0.9,
                            )}
                            style={lineTransitionStyle}
                        />
                    ))}
                    {lines
                        .filter(
                            (line) =>
                                line.dur &&
                                !isLineForcedInactive(line) &&
                                !isMatchedButInactive(line) &&
                                isLineAnimated(line),
                        )
                        .map((line) => (
                            <FlowPath
                                key={`flow-${line.key}`}
                                d={line.d}
                                stroke={line.color}
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray="2 8"
                                opacity={getLineOpacity(line, 0.95, 0.5)}
                                style={{
                                    ...lineTransitionStyle,
                                    animationDuration: line.dur,
                                    animationDelay: line.delay,
                                }}
                            />
                        ))}
                </FlowSvg>

                {trunkMidPoint && (
                    <Tooltip placement="top" title={HUB_TOOLTIP_TEXT}>
                        <LogoBadge
                            $x={trunkMidPoint.x}
                            $y={trunkMidPoint.y}
                            $interactive
                            $navigable={Boolean(onHubClick)}
                            style={badgeTransitionStyle}
                            onMouseEnter={handleHubMouseEnter}
                            onMouseLeave={handleHubMouseLeave}
                            onClick={handleHubClick}
                            role="button"
                            tabIndex={0}
                        >
                            <img
                                src="/assets/images/signal-sight.png"
                                alt="SignalSight"
                            />
                        </LogoBadge>
                    </Tooltip>
                )}

                <Column $width={columnWidth}>
                    <ColumnTitle>{sourcesTitle}</ColumnTitle>
                    {(() => {
                        const SourceCardList = compact
                            ? CompactCardList
                            : CardList;
                        return (
                            <SourceCardList>
                                {sourceGroups.length > 0 ? (
                                    <>
                                        {sourceGroups.map((group) =>
                                            compact
                                                ? renderCompactCard(
                                                      group,
                                                      "source",
                                                  )
                                                : renderCard(group, "source"),
                                        )}
                                        {sourceTrailingContent}
                                    </>
                                ) : (
                                    <AddCard ref={addSourceRef}>
                                        {emptySourceContent ?? (
                                            <StandartButton
                                                onClick={onAddSource}
                                                variant={
                                                    ButtonVariant.BLACK_OUTLINED
                                                }
                                                fullWidth
                                            >
                                                {addSourceLabel}
                                            </StandartButton>
                                        )}
                                    </AddCard>
                                )}
                            </SourceCardList>
                        );
                    })()}
                </Column>

                <Column $width={columnWidth}>
                    <ColumnTitle $align="right">{destinationsTitle}</ColumnTitle>
                    {(() => {
                        const DestinationCardList = compact
                            ? CompactCardList
                            : CardList;
                        return (
                            <DestinationCardList
                                $justify={compact ? "flex-end" : undefined}
                            >
                                {destinationGroups.length > 0 ? (
                                    <>
                                        {destinationGroups.map((group) =>
                                            compact
                                                ? renderCompactCard(
                                                      group,
                                                      "destination",
                                                  )
                                                : renderCard(
                                                      group,
                                                      "destination",
                                                  ),
                                        )}
                                        {destinationTrailingContent}
                                    </>
                                ) : (
                                    <AddCard ref={addDestinationRef}>
                                        {emptyDestinationContent ?? (
                                            <StandartButton
                                                onClick={onAddDestination}
                                                variant={
                                                    ButtonVariant.BLACK_OUTLINED
                                                }
                                                fullWidth
                                            >
                                                {addDestinationLabel}
                                            </StandartButton>
                                        )}
                                    </AddCard>
                                )}
                            </DestinationCardList>
                        );
                    })()}
                </Column>

                {compact && hoverOnlyLines.length > 0 && (
                    <HoverFlowSvg width={svgSize.width} height={svgSize.height}>
                        {hoverOnlyLines.map((line) => (
                            <BasePath
                                key={line.key}
                                d={line.d}
                                stroke={line.color}
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                opacity={getHoverOnlyLineOpacity(
                                    line,
                                    line.dur ? 0.25 : 0.9,
                                )}
                                style={lineTransitionStyle}
                            />
                        ))}
                        {hoverOnlyLines
                            .filter((line) => line.dur)
                            .map((line) => (
                                <FlowPath
                                    key={`hover-flow-${line.key}`}
                                    d={line.d}
                                    stroke={line.color}
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeDasharray="2 8"
                                    opacity={getHoverOnlyLineOpacity(
                                        line,
                                        0.95,
                                    )}
                                    style={{
                                        ...lineTransitionStyle,
                                        animationDuration: line.dur,
                                        animationDelay: line.delay,
                                    }}
                                />
                            ))}
                    </HoverFlowSvg>
                )}
            </FlowColumns>
        </FlowPanel>
    );
};

export default FlowDiagram;
